import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { 
  Table, Tag, Spin, Select, Input, Button, Space, DatePicker, 
  message, Row, Col, Card, Typography, Divider, Tooltip, InputNumber 
} from 'antd';
import { 
  MessageOutlined,
  LikeOutlined, 
  DislikeOutlined,
  WarningOutlined,
  FlagOutlined,
  ReloadOutlined,
  DownloadOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { getConversations, getConversationDetail, exportConversations } from '../../api/api';
import type { ConversationItem, ConversationDetailResponse } from '../../types/api-models';

dayjs.extend(relativeTime);

const { RangePicker } = DatePicker;

// Source badge component with brand-specific colors
const SourceBadge: React.FC<{ source: string }> = ({ source }) => {
  // Map different source types to appropriate colors
  let color = 'cyan'; // Default to cyan instead of black for better readability
  
  if (source?.toLowerCase().includes('url') || source?.toLowerCase().includes('web')) {
    color = 'blue';
  } else if (source?.toLowerCase().includes('pdf') || source?.toLowerCase().includes('document')) {
    color = 'green';
  } else if (source?.toLowerCase().includes('file') || source?.toLowerCase().includes('upload')) {
    color = 'purple';
  } else if (source?.toLowerCase().includes('api') || source?.toLowerCase().includes('feed')) {
    color = 'orange';
  } else if (source?.toLowerCase().includes('sitemap')) {
    color = 'geekblue';
  } else if (source?.toLowerCase().includes('webhook')) {
    color = 'magenta';
  }
  
  return <Tag color={color}>{source}</Tag>;
};
const { Text, Paragraph } = Typography;

type PeriodType = '30m' | '1h' | '3h' | '6h' | '12h' | '24h' | 'custom';

interface QAFeedProps {
  defaultPeriod?: PeriodType;
  isExpanded?: boolean;
  dashboardData?: any;
}

// Format date as relative time with fallback
const formatRelativeTime = (dateStr: string): string => {
  if (!dateStr) return '';
  
  try {
    const date = dayjs(dateStr);
    const now = dayjs();
    const diffDays = now.diff(date, 'day');
    
    if (diffDays < 7) {
      return date.fromNow();
    }
    
    return date.format('MMM DD, YYYY HH:mm');
  } catch {
    return dateStr;
  }
};

// Feedback icon component
const FeedbackIcon: React.FC<{ feedback?: 'like' | 'dislike' | null }> = ({ feedback }) => {
  if (!feedback) return null;
  
  if (feedback === 'like') {
    return <LikeOutlined style={{ color: '#52c41a', fontSize: 16 }} />;
  }
  
  return <DislikeOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />;
};

const QAFeed: React.FC<QAFeedProps> = ({
  defaultPeriod = '1h',
  isExpanded = false,
  dashboardData
}) => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState<ConversationItem[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [conversationDetail, setConversationDetail] = useState<ConversationDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  
  // Filters
  const [period, setPeriod] = useState<PeriodType>(defaultPeriod);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [search, setSearch] = useState('');
  const [feedbackFilter, setFeedbackFilter] = useState<string | undefined>(undefined);
  const [sourceFilter, setSourceFilter] = useState<string | undefined>(undefined);
  const [reportFilter, setReportFilter] = useState<string | undefined>(undefined);

  // Helper: Get date parameters from current filter state
  const getDateParameters = (): { 
    periodParam?: '30m' | '1h' | '3h' | '6h' | '12h' | '24h', 
    from_date?: string, 
    to_date?: string 
  } | null => {
    if (period === 'custom') {
      if (dateRange && dateRange[0] && dateRange[1]) {
        return {
          from_date: dateRange[0].toISOString(),
          to_date: dateRange[1].toISOString(),
          periodParam: undefined
        };
      } else {
        // Custom selected but no date range
        message.error('Please select a date range for custom period');
        return null;
      }
    } else {
      // Use predefined period
      return {
        periodParam: period as '30m' | '1h' | '3h' | '6h' | '12h' | '24h',
        from_date: undefined,
        to_date: undefined
      };
    }
  };

  // Fetch data - uses useRef to always have current filter values
  const fetchData = async (pageOverride?: number) => {
    setLoading(true);
    try {
      const dateParams = getDateParameters();
      if (!dateParams) {
        setLoading(false);
        return;
      }

      const pageToFetch = pageOverride ?? currentPage;

      const response = await getConversations(
        dateParams.periodParam,
        dateParams.from_date,
        dateParams.to_date,
        search || undefined,
        feedbackFilter as any,
        sourceFilter as any,
        isExpanded ? pageToFetch : 1,
        isExpanded ? 10 : 5,
        reportFilter as any
      );

      setData(response.items);
      setTotalCount(response.total_count);
      setTotalPages(response.total_pages);
      
      // Auto-select first conversation in expanded view
      if (isExpanded && response.items.length > 0 && !selectedConversationId) {
        handleSelectConversation(response.items[0].conversation_id);
      }
    } catch (error) {
      message.error('Failed to load conversations');
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Export to CSV
  const handleExport = async () => {
    setExporting(true);
    try {
      const dateParams = getDateParameters();
      if (!dateParams) {
        setExporting(false);
        return;
      }

      const blob = await exportConversations(
        dateParams.periodParam,
        dateParams.from_date,
        dateParams.to_date,
        search || undefined,
        feedbackFilter as any,
        sourceFilter as any,
        reportFilter as any
      );

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qa-feed_${dayjs().format('YYYYMMDD_HHmmss')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      message.success('Export completed successfully');
    } catch (error) {
      message.error('Failed to export conversations');
    } finally {
      setExporting(false);
    }
  };

  // Load conversation detail on selection
  const handleSelectConversation = async (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setDetailLoading(true);
    try {
      const detail = await getConversationDetail(conversationId);
      setConversationDetail(detail);
    } catch (error) {
      message.error('Failed to load conversation detail');
      setConversationDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  // Fetch on mount and when filters change
  useEffect(() => {
    // Skip initial fetch in expanded mode - wait for user to apply filters
    if (isExpanded) {
      // Auto-fetch on mount so the page isn't empty on first load
      fetchData(1);
      return;
    }
    
    // For collapsed view, use dashboard data
    if (dashboardData?.qa_feed) {
      setData(dashboardData.qa_feed.items);
      setTotalCount(dashboardData.qa_feed.total_count);
      setTotalPages(Math.ceil(dashboardData.qa_feed.total_count / 5));
    }
  }, [isExpanded, dashboardData]);

  // Fetch data when page changes (only in expanded view after initial load)
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchData(newPage);
  };

  // Handler for Apply Filters button - resets to page 1 and fetches
  const handleApplyFilters = () => {
    setCurrentPage(1);
    // Clear selected conversation when filters change
    setSelectedConversationId(null);
    setConversationDetail(null);
    fetchData(1);
  };

  // Columns for collapsed view
  const collapsedColumns: ColumnsType<ConversationItem> = [
    {
      title: 'Question',
      dataIndex: 'first_question',
      key: 'first_question',
      width: '45%',
      ellipsis: true,
      render: (text: string, record: ConversationItem) => (
        <Space>
          {record.has_issues && (
            <Tooltip title={`${record.flagged_count} flagged, ${record.report_count} reported`}>
              <FlagOutlined style={{ color: '#ff4d4f' }} />
            </Tooltip>
          )}
          <span>{text || 'N/A'}</span>
        </Space>
      ),
    },
    {
      title: 'Turns',
      dataIndex: 'turns',
      key: 'turns',
      width: '10%',
      align: 'center',
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: '15%',
      render: (source: string) => <SourceBadge source={source} />,
    },
    {
      title: 'Modified',
      dataIndex: 'last_activity',
      key: 'last_activity',
      width: '20%',
      render: (date: string) => formatRelativeTime(date),
    },
    {
      title: 'Feedback',
      dataIndex: 'latest_feedback',
      key: 'latest_feedback',
      width: '10%',
      align: 'center',
      render: (feedback: 'like' | 'dislike' | null) => <FeedbackIcon feedback={feedback} />,
    },
  ];

  // Empty state text
  const getEmptyText = () => {
    if (isExpanded) {
      return 'No conversations in selected period. Try adjusting filters.';
    }
    return `No conversations in last ${period}. Try expanding for more options.`;
  };

  // Loading spinner for collapsed view
  if (loading && !isExpanded) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Collapsed table view
  const tableComponent = (
    <Table
      columns={collapsedColumns}
      dataSource={data}
      rowKey="conversation_id"
      loading={loading && isExpanded ? { indicator: <Spin size="large" /> } : false}
      pagination={
        isExpanded
          ? {
              current: currentPage,
              pageSize: 10,
              total: totalCount,
              onChange: handlePageChange,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              showSizeChanger: false,
              position: ['bottomRight'],
            }
          : false
      }
      locale={{ emptyText: getEmptyText() }}
      size="small"
      onRow={(record) => ({
        onClick: () => isExpanded && handleSelectConversation(record.conversation_id),
        style: { 
          cursor: isExpanded ? 'pointer' : 'default',
          background: selectedConversationId === record.conversation_id ? '#f0f5ff' : undefined
        }
      })}
      footer={
        !isExpanded && totalCount > 0
          ? () => (
              <div style={{ textAlign: 'center', color: '#666', fontSize: 12 }}>
                Showing {data.length} of {totalCount} conversations (Click expand to view all)
              </div>
            )
          : undefined
      }
    />
  );

  // If not expanded, just return table
  if (!isExpanded) {
    return tableComponent;
  }

  // Expanded view with split panel
  return (
    <div>
      {/* Controls Row */}
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Space wrap>
          <span style={{ fontSize: 13, color: '#666' }}>Period:</span>
          <Select
            value={period}
            onChange={(value) => {
              setPeriod(value);
              if (value !== 'custom') setDateRange(null);
            }}
            style={{ width: 130 }}
            options={[
              { label: 'Last 30 min', value: '30m' },
              { label: 'Last 1 hour', value: '1h' },
              { label: 'Last 3 hours', value: '3h' },
              { label: 'Last 6 hours', value: '6h' },
              { label: 'Last 12 hours', value: '12h' },
              { label: 'Last 24 hours', value: '24h' },
              { label: 'Custom', value: 'custom' },
            ]}
          />
          {period === 'custom' && (
            <RangePicker
              showTime
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD HH:mm"
              style={{ width: 340 }}
              disabledDate={(current) => current && current > dayjs().endOf('day')}
            />
          )}
          
          <Input
            placeholder="Search conversations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          
          <Select
            value={feedbackFilter}
            onChange={setFeedbackFilter}
            style={{ width: 130 }}
            placeholder="All Feedback"
            allowClear
            options={[
              { value: 'positive', label: 'Positive' },
              { value: 'negative', label: 'Negative' },
              { value: 'none', label: 'No Feedback' },
              { value: 'any', label: 'Has Feedback' },
            ]}
          />
          
          <Select
            value={sourceFilter}
            onChange={setSourceFilter}
            style={{ width: 120 }}
            placeholder="All Sources"
            allowClear
            options={[
              { value: 'Web', label: 'Web' },
              { value: 'Modal', label: 'Modal' },
              { value: 'Others', label: 'Others' },
            ]}
          />
          
          <Select
            value={reportFilter}
            onChange={setReportFilter}
            style={{ width: 130 }}
            placeholder="All Reports"
            allowClear
            options={[
              { value: 'reported', label: 'Has Reports' },
              { value: 'none', label: 'No Reports' },
            ]}
          />
        </Space>
        
        <Space>
          <Button type="primary" onClick={handleApplyFilters} loading={loading}>
            Apply Filters
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => fetchData()} disabled={loading}>
            Refresh
          </Button>
          <Button 
            icon={<DownloadOutlined />} 
            onClick={handleExport}
            loading={exporting}
          >
            Export CSV
          </Button>
        </Space>
      </Space>

      {/* Split Panel Layout */}
      <Row gutter={16} style={{ minHeight: 600 }}>
        {/* Left Panel: Conversation List */}
        <Col span={10}>
          {tableComponent}
        </Col>
        
        {/* Right Panel: Conversation Thread */}
        <Col span={14}>
          {selectedConversationId ? (
            <Card 
              size="small" 
              title={
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                  <Space>
                    <Text strong>Conversation:</Text>
                    <Text code style={{ fontSize: 11 }}>{selectedConversationId}</Text>
                  </Space>
                  {conversationDetail && (conversationDetail.flagged_count > 0 || conversationDetail.report_count > 0) && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {conversationDetail.flagged_count > 0 && (
                        <span>
                          <WarningOutlined style={{ color: '#faad14' }} /> {conversationDetail.flagged_count} Flagged
                        </span>
                      )}
                      {conversationDetail.flagged_count > 0 && conversationDetail.report_count > 0 && ' • '}
                      {conversationDetail.report_count > 0 && (
                        <span>
                          <FlagOutlined style={{ color: '#ff4d4f' }} /> {conversationDetail.report_count} Reported
                        </span>
                      )}
                    </Text>
                  )}
                </Space>
              }
              style={{ height: 560, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
              bodyStyle={{ flex: 1, overflow: 'auto', padding: 16 }}
            >
              {detailLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Spin size="large" />
                </div>
              ) : conversationDetail ? (
                conversationDetail.messages.map((msg, index) => (
                <div key={`${msg.id}-${index}`} style={{ marginBottom: index < conversationDetail.messages.length - 1 ? 16 : 0 }}>
                  {/* Message Header */}
                  <Space size={4} style={{ marginBottom: 6 }}>
                    <Text strong style={{ fontSize: 12, color: msg.role === 'user' ? '#1890ff' : '#52c41a' }}>
                      {msg.role === 'user' ? 'User' : 'Assistant'}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      ({dayjs(msg.timestamp).format('HH:mm:ss')})
                    </Text>
                    {msg.role === 'assistant' && msg.response_time && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        • <ClockCircleOutlined /> {msg.response_time.toFixed(1)}s
                      </Text>
                    )}
                  </Space>
                  
                  {/* Message Content */}
                  <div 
                    style={{ 
                      marginBottom: 8, 
                      padding: 12, 
                      background: msg.role === 'user' ? '#f0f5ff' : '#f6ffed',
                      borderRadius: 6,
                      fontSize: 13,
                      lineHeight: 1.6,
                      overflowWrap: 'break-word',
                      wordBreak: 'break-word'
                    }}
                  >
                    {msg.role === 'assistant' ? (
                      <ReactMarkdown
                        components={{
                          a: ({ node, ...props }) => (
                            <a {...props} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }} />
                          ),
                          p: ({ node, ...props }) => (
                            <p {...props} style={{ margin: '0 0 8px 0' }} />
                          ),
                          ul: ({ node, ...props }) => (
                            <ul {...props} style={{ margin: '4px 0', paddingLeft: 20 }} />
                          ),
                          ol: ({ node, ...props }) => (
                            <ol {...props} style={{ margin: '4px 0', paddingLeft: 20 }} />
                          ),
                          li: ({ node, ...props }) => (
                            <li {...props} style={{ marginBottom: 2 }} />
                          ),
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                    ) : (
                      msg.content
                    )}
                  </div>
                  
                  {/* Feedback (Assistant only) */}
                  {msg.role === 'assistant' && msg.feedback && (
                    <div style={{ textAlign: 'right', marginBottom: 8 }}>
                      <FeedbackIcon feedback={msg.feedback} />
                      <Text type="secondary" style={{ marginLeft: 6, fontSize: 11 }}>
                        {msg.feedback === 'like' ? 'Liked' : 'Disliked'}
                      </Text>
                    </div>
                  )}
                  
                  {/* Flagged Warning */}
                  {msg.flagged && (
                    <div style={{ 
                      padding: 8, 
                      background: '#fff7e6', 
                      border: '1px solid #ffd591',
                      borderRadius: 4,
                      marginBottom: 8
                    }}>
                      <Space>
                        <WarningOutlined style={{ color: '#faad14' }} />
                        <Text strong style={{ fontSize: 12, color: '#faad14' }}>FLAGGED by system</Text>
                      </Space>
                    </div>
                  )}
                  
                  {/* Report Details */}
                  {msg.report && (
                    <div style={{ 
                      padding: 8, 
                      background: '#fff1f0', 
                      border: '1px solid #ffccc7',
                      borderRadius: 4,
                      marginBottom: 8
                    }}>
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Space>
                          <FlagOutlined style={{ color: '#ff4d4f' }} />
                          <Text strong style={{ fontSize: 12, color: '#ff4d4f' }}>REPORTED</Text>
                        </Space>
                        <Text style={{ fontSize: 11 }}>
                          <strong>Subject:</strong> {msg.report.subject}
                        </Text>
                        <Text style={{ fontSize: 11 }}>
                          <strong>Description:</strong> {msg.report.description}
                        </Text>
                      </Space>
                    </div>
                  )}
                  
                  {/* Divider between messages */}
                  {index < conversationDetail.messages.length - 1 && <Divider style={{ margin: '12px 0' }} />}
                </div>
              ))
              ) : null}
            </Card>
          ) : (
            <Card size="small" style={{ height: 560, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center', color: '#999' }}>
                <MessageOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                <div>Select a conversation to view details</div>
              </div>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default QAFeed;

